'use client';

import React from 'react';
import { useRouter } from '@/lib/router';
import {
  currentTenant, tenantAnalytics, agents, waSessions, customers,
  knowledgeBases, conversations, toolApprovals, integrations,
  invoices, teamMembers, retailProducts, formatRupiah, formatNumber, plans,
} from '@/lib/mock-data';
import {
  StatsCard, StatusBadge, QuotaBar, PageHeader, DataTable,
  EmptyState, TabFilter, SearchInput, IconSvg,
  Modal, FormField, FormInput, FormSelect, FormTextarea,
  Toggle, ConfirmDialog, useToast, Toast, DropdownMenu, DropdownItem,
} from '@/components/shared';

// ============================================
// 1. TENANT DASHBOARD PAGE
// ============================================
export function TenantDashboardPage() {
  const navigate = useRouter((s) => s.navigate);
  const a = tenantAnalytics;
  const maxConv = Math.max(...a.chart_7days.map(d => d.conversations));

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h2 className="text-xl font-bold text-slate-50">Selamat datang, Budi! 👋</h2>
        <p className="text-sm text-slate-400 mt-1">Berikut ringkasan aktivitas bisnis Anda hari ini.</p>
      </div>

      {/* Subscription Warning */}
      {currentTenant.days_remaining <= 30 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm text-amber-400">⚠️ Langganan berakhir dalam {currentTenant.days_remaining} hari</p>
          <button onClick={() => navigate('/tenant/subscription')} className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
            Perpanjang
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatsCard
          title="Percakapan Hari Ini"
          value={String(a.conversations.today)}
          trend={`${a.conversations.trend_percent}%`}
          trendUp={true}
          iconBg="bg-orange-500/20" iconColor="text-orange-400"
          icon={<IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></IconSvg>}
        />
        <StatsCard
          title="AI Aktif"
          value={`${a.agents.active}/${a.agents.total}`}
          iconBg="bg-violet-500/20" iconColor="text-violet-400"
          icon={<IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></IconSvg>}
          onClick={() => navigate('/tenant/agents')}
        />
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <p className="text-sm text-slate-400 mb-2">Token Usage</p>
          <p className="text-2xl font-bold text-slate-50">{formatNumber(a.token_usage.used)}<span className="text-sm text-slate-500 font-normal">/{formatNumber(a.token_usage.total)}</span></p>
          <div className="mt-3">
            <QuotaBar used={a.token_usage.used} total={a.token_usage.total} />
          </div>
        </div>
        <StatsCard
          title="Pelanggan Baru"
          value={`+${a.new_customers.this_week}`}
          trend={`${a.new_customers.trend_percent}%`}
          trendUp={true}
          iconBg="bg-emerald-500/20" iconColor="text-emerald-400"
          icon={<IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" /></IconSvg>}
          onClick={() => navigate('/tenant/customers')}
        />
        <StatsCard
          title="Response Time"
          value={`${a.response_time.avg_seconds}s`}
          trend="0.3s"
          trendUp={false}
          iconBg="bg-sky-500/20" iconColor="text-sky-400"
          icon={<IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></IconSvg>}
        />
        <StatsCard
          title="Pending Approvals"
          value={String(a.pending_approvals)}
          iconBg="bg-amber-500/20" iconColor="text-amber-400"
          icon={<IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></IconSvg>}
          onClick={() => navigate('/tenant/tool-approvals')}
        />
      </div>

      {/* Chart + Agent/WA status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart */}
        <div className="lg:col-span-2 bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Percakapan 7 Hari Terakhir</h3>
          <div className="flex items-end gap-2 h-40">
            {a.chart_7days.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-xs text-slate-400">{d.conversations}</span>
                <div
                  className="w-full bg-orange-500/80 hover:bg-orange-500 rounded-t-md transition-colors min-h-[4px]"
                  style={{ height: `${(d.conversations / maxConv) * 120}px` }}
                />
                <span className="text-xs text-slate-500">{d.day}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Agent Status */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-slate-200 mb-4">Status Agent</h3>
          <div className="space-y-3">
            {agents.map((ag) => (
              <div key={ag.id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${ag.status === 'active' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-200">{ag.name}</p>
                    <p className="text-xs text-slate-500">{ag.ai_model}</p>
                  </div>
                </div>
                <StatusBadge status={ag.status} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WA Sessions */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-200">WhatsApp Sessions</h3>
          <button onClick={() => navigate('/tenant/wa-sessions')} className="text-xs text-orange-400 hover:text-orange-300 transition-colors">Lihat semua →</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {waSessions.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
              <div className={`w-2.5 h-2.5 rounded-full ${s.status === 'connected' ? 'bg-emerald-400' : 'bg-slate-500'}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">{s.phone}</p>
                <p className="text-xs text-slate-500">{s.messages_today} pesan hari ini</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Agent Form Modal (shared between create/edit)
// ============================================
function AgentFormModal({ open, onClose, agent, onSave }: {
  open: boolean;
  onClose: () => void;
  agent: typeof agents[0] | null;
  onSave: () => void;
}) {
  const { toast, show } = useToast();
  const [name, setName] = React.useState(agent?.name || '');
  const [desc, setDesc] = React.useState(agent?.description || '');
  const [provider, setProvider] = React.useState(agent?.ai_provider || 'OpenAI');
  const [model, setModel] = React.useState(agent?.ai_model || 'gpt-4o');
  const [prompt, setPrompt] = React.useState(agent?.system_prompt || '');
  const [temp, setTemp] = React.useState(agent?.temperature || 0.7);
  const [maxTokens, setMaxTokens] = React.useState(String(agent?.max_tokens || 500));
  const [isActive, setIsActive] = React.useState(agent?.status === 'active' || true);

  React.useEffect(() => {
    if (agent) {
      setName(agent.name);
      setDesc(agent.description);
      setProvider(agent.ai_provider);
      setModel(agent.ai_model);
      setPrompt(agent.system_prompt);
      setTemp(agent.temperature);
      setMaxTokens(String(agent.max_tokens));
      setIsActive(agent.status === 'active');
    } else {
      setName(''); setDesc(''); setProvider('OpenAI'); setModel('gpt-4o');
      setPrompt(''); setTemp(0.7); setMaxTokens('500'); setIsActive(true);
    }
  }, [agent, open]);

  const handleSave = () => {
    onSave();
    onClose();
    show(agent ? 'Agent berhasil diperbarui!' : 'Agent baru berhasil dibuat!');
  };

  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title={agent ? 'Edit Agent' : 'Buat Agent Baru'} size="lg" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Nama Agent" required><FormInput value={name} onChange={e => setName(e.target.value)} placeholder="Nama agent..." /></FormField>
            <FormField label="AI Provider" required>
              <FormSelect value={provider} onChange={e => setProvider(e.target.value)}>
                <option value="OpenAI">OpenAI</option>
                <option value="Anthropic">Anthropic</option>
              </FormSelect>
            </FormField>
            <FormField label="AI Model" required>
              <FormSelect value={model} onChange={e => setModel(e.target.value)}>
                <option value="gpt-4o">gpt-4o</option>
                <option value="gpt-4o-mini">gpt-4o-mini</option>
                <option value="claude-3-haiku">claude-3-haiku</option>
              </FormSelect>
            </FormField>
            <FormField label="Max Tokens">
              <FormInput type="number" value={maxTokens} onChange={e => setMaxTokens(e.target.value)} />
            </FormField>
          </div>
          <FormField label="Deskripsi"><FormInput value={desc} onChange={e => setDesc(e.target.value)} placeholder="Deskripsi agent..." /></FormField>
          <FormField label="System Prompt"><FormTextarea rows={4} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="System prompt..." /></FormField>
          <FormField label={`Temperature: ${temp}`} hint="Precise (0) → Creative (1)">
            <input type="range" min="0" max="1" step="0.1" value={temp} onChange={e => setTemp(Number(e.target.value))} className="w-full accent-orange-500" />
          </FormField>
          <Toggle checked={isActive} onChange={setIsActive} label="Agent Aktif" />
        </div>
      </Modal>
    </>
  );
}

// ============================================
// 2. TENANT AGENTS PAGE
// ============================================
export function TenantAgentsPage() {
  const navigate = useRouter((s) => s.navigate);
  const activeCount = agents.filter(a => a.status === 'active').length;
  const { toast, show } = useToast();
  const [showCreate, setShowCreate] = React.useState(false);
  const [editAgent, setEditAgent] = React.useState<typeof agents[0] | null>(null);

  return (
    <div className="space-y-6">
      <Toast toast={toast} />
      <PageHeader
        title="AI Agents"
        subtitle={`${agents.length} agent · ${activeCount} aktif`}
        actions={
          <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
            + Buat Agent Baru
          </button>
        }
      />

      <AgentFormModal open={showCreate} onClose={() => setShowCreate(false)} agent={null} onSave={() => {}} />
      <AgentFormModal open={!!editAgent} onClose={() => setEditAgent(null)} agent={editAgent} onSave={() => {}} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents.map((ag) => (
          <div key={ag.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${ag.status === 'active' ? 'bg-violet-400' : 'bg-amber-400'}`} />
                <h3 className="text-base font-semibold text-slate-100">{ag.name}</h3>
              </div>
              <StatusBadge status={ag.status} />
            </div>
            <p className="text-sm text-slate-400 mb-3 line-clamp-2">{ag.description}</p>
            <p className="text-xs text-slate-500 mb-3">{ag.ai_provider} · {ag.ai_model}</p>

            {/* Meta */}
            <div className="flex items-center gap-4 text-xs text-slate-400 mb-3">
              <span>📋 {ag.rules_count} rules</span>
              <span>🔧 {ag.tools_count} tools</span>
              <span>📡 {ag.channels_count} channels</span>
            </div>

            {/* Token bar */}
            <div className="mb-3">
              <QuotaBar used={ag.token_today} total={ag.token_limit} label={`${formatNumber(ag.token_today)}/${formatNumber(ag.token_limit)} tokens`} />
            </div>

            {/* Channel badges */}
            {ag.channels.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {ag.channels.map((ch, ci) => (
                  <span key={ci} className={`text-xs px-2 py-0.5 rounded-full ${ch.type === 'whatsapp' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-sky-500/20 text-sky-400'}`}>
                    {ch.type === 'whatsapp' ? '📱 WA' : '✈️ TG'}
                  </span>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-2 pt-3 border-t border-slate-700">
              <button onClick={() => setEditAgent(ag)} className="px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Edit</button>
              <button onClick={() => navigate('/tenant/agents/' + ag.id)} className="px-3 py-1.5 text-xs font-medium text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors">
                Buka Detail →
              </button>
            </div>
          </div>
        ))}

        {/* Create New Agent */}
        <button onClick={() => setShowCreate(true)} className="border-2 border-dashed border-slate-700 rounded-xl p-5 flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-orange-500/50 hover:text-orange-400 transition-all min-h-[200px]">
          <span className="text-3xl">+</span>
          <span className="text-sm font-medium">Buat Agent Baru</span>
        </button>
      </div>
    </div>
  );
}

// ============================================
// 3. TENANT AGENT DETAIL PAGE
// ============================================
export function TenantAgentDetailPage() {
  const navigate = useRouter((s) => s.navigate);
  const params = useRouter((s) => s.params);
  const agentId = params.id || 'ag-001';
  const agent = agents.find(a => a.id === agentId) || agents[0];
  const [tab, setTab] = React.useState('info');
  const [enabled, setEnabled] = React.useState(agent.status === 'active');
  const [temperature, setTemperature] = React.useState(agent.temperature);
  const [name, setName] = React.useState(agent.name);
  const [desc, setDesc] = React.useState(agent.description);
  const [prompt, setPrompt] = React.useState(agent.system_prompt);
  const [maxTokens, setMaxTokens] = React.useState(String(agent.max_tokens));

  const tabs = [
    { label: 'Info Dasar', value: 'info' },
    { label: 'Rules', value: 'rules' },
    { label: 'Tools', value: 'tools' },
    { label: 'Channel', value: 'channel' },
    { label: 'Usage Log', value: 'usage' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/tenant/agents')} className="text-slate-400 hover:text-white transition-colors">
            <IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></IconSvg>
          </button>
          <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-violet-400' : 'bg-amber-400'}`} />
          <h1 className="text-xl font-bold text-slate-50">{name}</h1>
          <StatusBadge status={agent.status} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400">{enabled ? 'Aktif' : 'Nonaktif'}</span>
          <button
            onClick={() => setEnabled(!enabled)}
            className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-violet-500' : 'bg-slate-600'}`}
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-5.5' : 'translate-x-0.5'}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <TabFilter tabs={tabs} active={tab} onChange={setTab} />

      {/* Tab Content */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        {tab === 'info' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Nama Agent</label>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Deskripsi</label>
              <input value={desc} onChange={e => setDesc(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-slate-300 mb-1.5">System Prompt</label>
              <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={4} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Temperature: {temperature}</label>
              <input type="range" min="0" max="1" step="0.1" value={temperature} onChange={e => setTemperature(Number(e.target.value))} className="w-full accent-orange-500" />
              <div className="flex justify-between text-xs text-slate-500 mt-1"><span>Precise (0)</span><span>Creative (1)</span></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Max Tokens</label>
              <input type="number" value={maxTokens} onChange={e => setMaxTokens(e.target.value)} className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Provider</label>
              <select className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
                <option>OpenAI</option>
                <option>Anthropic</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Model</label>
              <select className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50">
                <option>gpt-4o</option>
                <option>gpt-4o-mini</option>
                <option>claude-3-haiku</option>
              </select>
            </div>
          </div>
        )}

        {tab === 'rules' && (
          <div className="space-y-3">
            {agent.rules.length > 0 ? agent.rules.map((rule) => (
              <div key={rule.id} className="flex items-start justify-between gap-3 p-4 bg-slate-900/50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-amber-400">{rule.condition}</p>
                  <p className="text-sm text-slate-400 mt-1">→ {rule.action}</p>
                </div>
                <button className="text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded hover:bg-red-500/10 transition-colors">🗑 Hapus</button>
              </div>
            )) : (
              <EmptyState title="Belum ada rules" description="Tambahkan rules untuk mengatur perilaku agent Anda." />
            )}
            <button className="mt-3 px-4 py-2 text-sm font-medium text-orange-400 border border-orange-500/30 hover:bg-orange-500/10 rounded-lg transition-colors">
              + Tambah Rule
            </button>
          </div>
        )}

        {tab === 'tools' && (
          <div className="space-y-3">
            {agent.tools.length > 0 ? agent.tools.map((tool) => (
              <div key={tool.id} className="flex items-center justify-between gap-3 p-4 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-lg">🔧</span>
                  <div>
                    <p className="text-sm font-medium text-slate-200">{tool.display_name}</p>
                    <p className="text-xs text-slate-500">{tool.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tool.requires_approval ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    {tool.requires_approval ? 'Perlu Approval' : 'Auto'}
                  </span>
                  <button className="text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded hover:bg-red-500/10 transition-colors">Hapus</button>
                </div>
              </div>
            )) : (
              <EmptyState title="Belum ada tools" description="Hubungkan tools untuk memberikan kemampuan pada agent." />
            )}
          </div>
        )}

        {tab === 'channel' && (
          <div className="space-y-3">
            {agent.channels.length > 0 ? agent.channels.map((ch, ci) => (
              <div key={ci} className="flex items-center justify-between gap-3 p-4 bg-slate-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{ch.type === 'whatsapp' ? '📱' : '✈️'}</span>
                  <div>
                    <p className="text-sm font-medium text-slate-200">{ch.type === 'whatsapp' ? ch.number : ch.username}</p>
                    <p className="text-xs text-slate-500">{ch.type}</p>
                  </div>
                </div>
                <StatusBadge status={ch.status} />
              </div>
            )) : (
              <EmptyState title="Belum ada channel" description="Hubungkan channel komunikasi ke agent ini." />
            )}
            <button className="mt-3 px-4 py-2 text-sm font-medium text-orange-400 border border-orange-500/30 hover:bg-orange-500/10 rounded-lg transition-colors">
              + Tambah Channel
            </button>
          </div>
        )}

        {tab === 'usage' && (
          <div>
            {agent.usage_logs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Tanggal</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Tokens</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agent.usage_logs.map((log, i) => (
                      <tr key={i} className="border-b border-slate-700/50">
                        <td className="px-4 py-3 text-slate-300">{log.date}</td>
                        <td className="px-4 py-3 text-slate-300">{formatNumber(log.tokens)}</td>
                        <td className="px-4 py-3 text-slate-300">{formatNumber(log.requests)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="Belum ada data" description="Usage log akan muncul setelah agent digunakan." />
            )}
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition-colors">
          Simpan Perubahan
        </button>
      </div>
    </div>
  );
}

// ============================================
// Channel Create/Edit/Configure Modals
// ============================================
function ChannelFormModal({ open, onClose, channel, onSave }: {
  open: boolean; onClose: () => void;
  channel: { type: string; name: string; icon: string; status: string; sessions: number; agent: string; phone?: string; botToken?: string; botUsername?: string; widgetName?: string; position?: string; waSessionId?: string } | null;
  onSave: () => void;
}) {
  const { toast, show } = useToast();
  const [chType, setChType] = React.useState(channel?.type || 'whatsapp');
  const [waSessionId, setWaSessionId] = React.useState(channel?.waSessionId || '');
  const [botToken, setBotToken] = React.useState(channel?.botToken || '');
  const [botUsername, setBotUsername] = React.useState(channel?.botUsername || '');
  const [widgetName, setWidgetName] = React.useState(channel?.widgetName || '');
  const [position, setPosition] = React.useState(channel?.position || 'kanan_bawah');

  React.useEffect(() => {
    if (channel) {
      setChType(channel.type);
      setWaSessionId(channel.waSessionId || '');
      setBotToken(channel.botToken || '');
      setBotUsername(channel.botUsername || '');
      setWidgetName(channel.widgetName || '');
      setPosition(channel.position || 'kanan_bawah');
    } else {
      setChType('whatsapp'); setWaSessionId(''); setBotToken('');
      setBotUsername(''); setWidgetName(''); setPosition('kanan_bawah');
    }
  }, [channel, open]);

  const handleSave = () => { onSave(); onClose(); show(channel ? 'Channel berhasil diperbarui!' : 'Channel berhasil dibuat!'); };

  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title={channel ? 'Edit Channel' : 'Tambah Channel Baru'} size="default" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Channel Type" required>
            <FormSelect value={chType} onChange={e => setChType(e.target.value)} disabled={!!channel}>
              <option value="whatsapp">📱 WhatsApp</option>
              <option value="telegram">✈️ Telegram</option>
              <option value="webchat">💬 Web Chat</option>
            </FormSelect>
          </FormField>
          {chType === 'whatsapp' && (
            <FormField label="WA Session" required>
              <FormSelect value={waSessionId} onChange={e => setWaSessionId(e.target.value)}>
                <option value="">Pilih session...</option>
                {waSessions.map(s => (
                  <option key={s.id} value={s.id}>{s.phone} ({s.status})</option>
                ))}
              </FormSelect>
            </FormField>
          )}
          {chType === 'telegram' && (
            <>
              <FormField label="Bot Token" required><FormInput value={botToken} onChange={e => setBotToken(e.target.value)} placeholder="123456:ABC-DEF..." /></FormField>
              <FormField label="Bot Username" required><FormInput value={botUsername} onChange={e => setBotUsername(e.target.value)} placeholder="@MyBot" /></FormField>
            </>
          )}
          {chType === 'webchat' && (
            <>
              <FormField label="Widget Name" required><FormInput value={widgetName} onChange={e => setWidgetName(e.target.value)} placeholder="Chat Widget" /></FormField>
              <FormField label="Posisi">
                <FormSelect value={position} onChange={e => setPosition(e.target.value)}>
                  <option value="kanan_bawah">Kanan Bawah</option>
                  <option value="kiri_bawah">Kiri Bawah</option>
                </FormSelect>
              </FormField>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

function ChannelConfigureModal({ open, onClose, channel }: {
  open: boolean; onClose: () => void;
  channel: { type: string; name: string; icon: string; status: string; sessions: number; agent: string } | null;
}) {
  const embedCode = '<script src="https://rekanku.id/widget.js" data-token="tk_xxx"></script>';
  return (
    <Modal open={open} onClose={onClose} title="Konfigurasi Channel" subtitle={channel?.name} size="default">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><p className="text-xs text-slate-500 mb-1">Tipe</p><p className="text-sm text-slate-200">{channel?.icon} {channel?.type}</p></div>
          <div><p className="text-xs text-slate-500 mb-1">Status</p><StatusBadge status={channel?.status || 'standby'} /></div>
          <div><p className="text-xs text-slate-500 mb-1">Sessions</p><p className="text-sm text-slate-200">{channel?.sessions}</p></div>
          <div><p className="text-xs text-slate-500 mb-1">Agent</p><p className="text-sm text-slate-200">{channel?.agent}</p></div>
        </div>
        {channel?.type === 'webchat' && (
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Embed Code</p>
            <div className="bg-slate-900 rounded-lg p-3">
              <pre className="text-xs text-emerald-400 font-mono break-all">{embedCode}</pre>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================
// 4. TENANT CHANNELS PAGE
// ============================================
export function TenantChannelsPage() {
  const { toast, show } = useToast();
  const [showCreate, setShowCreate] = React.useState(false);
  const [editCh, setEditCh] = React.useState<{ type: string; name: string; icon: string; status: string; sessions: number; agent: string } | null>(null);
  const [configCh, setConfigCh] = React.useState<{ type: string; name: string; icon: string; status: string; sessions: number; agent: string } | null>(null);
  const channels = [
    { type: 'whatsapp', name: 'WhatsApp Business', icon: '📱', status: 'connected', sessions: 2, agent: 'Asisten Toko Budi, Budi Info Bot' },
    { type: 'telegram', name: 'Telegram Bot', icon: '✈️', status: 'connected', sessions: 1, agent: 'Asisten Toko Budi' },
    { type: 'webchat', name: 'Web Chat Widget', icon: '💬', status: 'standby', sessions: 0, agent: '-' },
  ];

  return (
    <div className="space-y-6">
      <Toast toast={toast} />
      <PageHeader title="Communication Channels" actions={
        <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
          + Tambah Channel
        </button>
      } />

      <ChannelFormModal open={showCreate} onClose={() => setShowCreate(false)} channel={null} onSave={() => {}} />
      <ChannelFormModal open={!!editCh} onClose={() => setEditCh(null)} channel={editCh} onSave={() => {}} />
      <ChannelConfigureModal open={!!configCh} onClose={() => setConfigCh(null)} channel={configCh} />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {channels.map((ch, i) => (
          <div key={i} className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center text-2xl">{ch.icon}</div>
              <div>
                <h3 className="text-base font-semibold text-slate-100">{ch.name}</h3>
                <StatusBadge status={ch.status} />
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">Sessions</span>
                <span className="text-slate-200">{ch.sessions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Agent</span>
                <span className="text-slate-200 text-right max-w-[200px] truncate">{ch.agent}</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-700 flex items-center gap-3">
              <button onClick={() => setConfigCh(ch)} className="text-xs text-orange-400 hover:text-orange-300 transition-colors">Configure →</button>
              <button onClick={() => setEditCh(ch)} className="text-xs text-slate-400 hover:text-slate-300 transition-colors">Edit</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// WA Session Create/Detail Modals
// ============================================
function WACreateSessionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast, show } = useToast();
  const [phone, setPhone] = React.useState('');
  const handleSave = () => { onClose(); show('Session berhasil dibuat!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title="Buat Session Baru" subtitle="Hubungkan nomor WhatsApp baru" size="default" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Nomor Telepon" required><FormInput value={phone} onChange={e => setPhone(e.target.value)} placeholder="+62 8xx-xxxx-xxxx" /></FormField>
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 flex flex-col items-center">
            <h4 className="text-sm font-semibold text-slate-200 mb-3">Scan QR Code</h4>
            <div className="w-40 h-40 bg-slate-800 border border-slate-600 rounded-xl flex items-center justify-center mb-3">
              <div className="text-center text-slate-500">
                <div className="text-4xl mb-1">📱</div>
                <p className="text-xs">QR Code Placeholder</p>
              </div>
            </div>
            <p className="text-xs text-slate-500">Buka WhatsApp → Perangkat Tertaut → Scan kode</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

function WAViewSessionModal({ open, onClose, session }: {
  open: boolean; onClose: () => void;
  session: typeof waSessions[0] | null;
}) {
  if (!session) return null;
  const logs = ['Session connected', 'Message sent to +6281...', 'Received message from +6282...', 'Auto-reply triggered'];
  return (
    <Modal open={open} onClose={onClose} title="Detail Session" subtitle={session.phone} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Nomor</p>
            <p className="text-sm font-medium text-slate-200">{session.phone}</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Status</p>
            <StatusBadge status={session.status} />
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Durasi</p>
            <p className="text-sm font-medium text-slate-200">{session.session_duration}</p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">Pesan Hari Ini</p>
            <p className="text-sm font-medium text-slate-200">{session.messages_today}</p>
          </div>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1.5">Last Activity</p>
          <p className="text-sm text-slate-200">{session.last_activity.replace('T', ' ')}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-2">Session Logs</p>
          <div className="bg-slate-900 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1">
            {logs.map((log, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                <span className="text-slate-600">{String(i + 1).padStart(2, '0')}</span>
                <span>{log}</span>
              </div>
            ))}
          </div>
        </div>
        {session.status === 'connected' && (
          <div className="flex gap-2 pt-2">
            <button className="px-4 py-2 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors">Stop</button>
            <button className="px-4 py-2 text-xs font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Restart</button>
            <button className="px-4 py-2 text-xs font-medium text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-colors">Logout</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================
// 5. TENANT WA SESSIONS PAGE
// ============================================
export function TenantWASessionsPage() {
  const { toast, show } = useToast();
  const [showCreate, setShowCreate] = React.useState(false);
  const [viewSession, setViewSession] = React.useState<typeof waSessions[0] | null>(null);

  return (
    <div className="space-y-6">
      <Toast toast={toast} />
      <WACreateSessionModal open={showCreate} onClose={() => setShowCreate(false)} />
      <WAViewSessionModal open={!!viewSession} onClose={() => setViewSession(null)} session={viewSession} />

      <PageHeader title="WhatsApp Sessions" actions={
        <button onClick={() => setShowCreate(true)} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
          + Buat Session Baru
        </button>
      } />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {waSessions.map((s) => (
          <div key={s.id} onClick={() => setViewSession(s)} className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all cursor-pointer">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${s.status === 'connected' ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                <span className="text-base font-semibold text-slate-100">{s.phone}</span>
              </div>
              <StatusBadge status={s.status} />
            </div>
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <span className="text-slate-400">Durasi</span>
                <span className="text-slate-200">{s.session_duration}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Pesan Hari Ini</span>
                <span className="text-slate-200">{s.messages_today}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Terakhir</span>
                <span className="text-slate-200">{s.last_activity.split('T')[0]}</span>
              </div>
            </div>
            <div className="flex gap-2 pt-3 border-t border-slate-700" onClick={e => e.stopPropagation()}>
              <button onClick={() => setViewSession(s)} className="px-3 py-1.5 text-xs font-medium text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors">Detail →</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// 6. TENANT CUSTOMERS PAGE
// ============================================
export function TenantCustomersPage() {
  const [search, setSearch] = React.useState('');
  const [roleFilter, setRoleFilter] = React.useState('all');
  const [selected, setSelected] = React.useState<typeof customers[0] | null>(null);

  const filtered = customers.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search) || c.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || c.role === roleFilter;
    return matchSearch && matchRole;
  });

  const columns = [
    {
      key: 'name', label: 'Nama',
      render: (c: typeof customers[0]) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-xs font-bold text-orange-400">
            {c.name.split(' ').map(n => n[0]).join('')}
          </div>
          <span className="font-medium text-slate-100">{c.name}</span>
        </div>
      ),
    },
    { key: 'phone', label: 'Telepon' },
    { key: 'email', label: 'Email' },
    {
      key: 'role', label: 'Role',
      render: (c: typeof customers[0]) => <StatusBadge status={c.role} />,
    },
    { key: 'total_orders', label: 'Total Orders' },
    { key: 'last_contact', label: 'Terakhir Kontak' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Pelanggan (1,847)" actions={
        <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
          + Tambah Pelanggan
        </button>
      } />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 max-w-sm">
          <SearchInput placeholder="Cari nama, telepon, email..." value={search} onChange={setSearch} />
        </div>
        <TabFilter tabs={[
          { label: 'Semua', value: 'all' },
          { label: 'VIP', value: 'VIP' },
          { label: 'Regular', value: 'Regular' },
          { label: 'Baru', value: 'New' },
        ]} active={roleFilter} onChange={setRoleFilter} />
      </div>

      <div className="relative">
        <DataTable columns={columns} data={filtered} onRowClick={(c) => setSelected(c as typeof customers[0])} />

        {/* Slide-over Detail Panel */}
        {selected && (
          <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setSelected(null)}>
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative w-full max-w-md bg-slate-800 border-l border-slate-700 p-6 overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-slate-50">Detail Pelanggan</h2>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-white">
                  <IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></IconSvg>
                </button>
              </div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center text-xl font-bold text-orange-400">
                  {selected.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="text-base font-semibold text-slate-100">{selected.name}</p>
                  <StatusBadge status={selected.role} />
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Telepon', value: selected.phone },
                  { label: 'Email', value: selected.email },
                  { label: 'Total Orders', value: String(selected.total_orders) },
                  { label: 'Total Spent', value: formatRupiah(selected.total_spent) },
                  { label: 'Terakhir Kontak', value: selected.last_contact },
                ].map((item, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-slate-700/50">
                    <span className="text-sm text-slate-400">{item.label}</span>
                    <span className="text-sm text-slate-200">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex gap-2">
                <button className="flex-1 px-4 py-2 text-sm font-medium text-orange-400 border border-orange-500/30 hover:bg-orange-500/10 rounded-lg transition-colors">
                  Kirim Pesan
                </button>
                <button className="flex-1 px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// 7. TENANT KNOWLEDGE BASE PAGE
// ============================================
export function TenantKnowledgeBasePage() {
  const [expanded, setExpanded] = React.useState<string | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader title="Knowledge Base" actions={
        <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
          + Buat KB Baru
        </button>
      } />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {knowledgeBases.map((kb) => (
          <div key={kb.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-100">{kb.name}</h3>
              <StatusBadge status={kb.status} />
            </div>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center p-2 bg-slate-900/50 rounded-lg">
                <p className="text-lg font-bold text-slate-100">{kb.document_count}</p>
                <p className="text-xs text-slate-500">Dokumen</p>
              </div>
              <div className="text-center p-2 bg-slate-900/50 rounded-lg">
                <p className="text-lg font-bold text-slate-100">{formatNumber(kb.chunks_count)}</p>
                <p className="text-xs text-slate-500">Chunks</p>
              </div>
              <div className="text-center p-2 bg-slate-900/50 rounded-lg">
                <p className="text-lg font-bold text-slate-100">{kb.size_mb} MB</p>
                <p className="text-xs text-slate-500">Size</p>
              </div>
            </div>

            {kb.used_by.length > 0 && (
              <p className="text-xs text-slate-400 mb-3">Dipakai oleh: <span className="text-violet-400">{kb.used_by.join(', ')}</span></p>
            )}

            <button
              onClick={() => setExpanded(expanded === kb.id ? null : kb.id)}
              className="text-xs text-orange-400 hover:text-orange-300 transition-colors mb-2"
            >
              {expanded === kb.id ? '▾ Sembunyikan dokumen' : '▸ Lihat dokumen'}
            </button>

            {expanded === kb.id && (
              <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                {kb.documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm">📄</span>
                      <span className="text-sm text-slate-300 truncate">{doc.name}</span>
                      <span className="text-xs text-slate-500">{doc.size_mb} MB</span>
                    </div>
                    <StatusBadge status={doc.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Create New KB */}
        <button className="border-2 border-dashed border-slate-700 rounded-xl p-5 flex flex-col items-center justify-center gap-2 text-slate-500 hover:border-orange-500/50 hover:text-orange-400 transition-all min-h-[180px]">
          <span className="text-3xl">+</span>
          <span className="text-sm font-medium">Buat Knowledge Base Baru</span>
        </button>
      </div>
    </div>
  );
}

// ============================================
// Conversation Detail Modal
// ============================================
function ConversationDetailModal({ open, onClose, conv }: {
  open: boolean; onClose: () => void;
  conv: typeof conversations[0] | null;
}) {
  if (!conv) return null;
  return (
    <Modal open={open} onClose={onClose} title={conv.customer.name} subtitle={conv.customer.phone} size="lg">
      <div className="space-y-4">
        {/* Customer Info */}
        <div className="flex items-center gap-3 p-3 bg-slate-900/50 rounded-lg">
          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-sm font-bold text-orange-400">{conv.customer.avatar}</div>
          <div>
            <p className="text-sm font-medium text-slate-200">{conv.customer.name}</p>
            <p className="text-xs text-slate-500">via {conv.channel === 'whatsapp' ? '📱 WA' : '✈️ TG'} · Agent: {conv.agent}</p>
          </div>
          <div className="ml-auto"><StatusBadge status={conv.status} /></div>
        </div>
        {/* AI Summary */}
        <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
          <p className="text-xs text-violet-400"><span className="font-medium">🧠 AI Summary:</span> {conv.ai_summary}</p>
        </div>
        {/* Messages */}
        <div className="space-y-3 max-h-72 overflow-y-auto">
          {conv.messages.map((msg) => {
            const isCustomer = msg.sender === 'customer';
            const isSystem = msg.sender === 'system' || msg.type === 'system';
            const isToolCall = msg.type === 'tool_call';
            if (isSystem) return <div key={msg.id} className="flex justify-center"><span className="text-xs text-slate-500 bg-slate-700/50 px-3 py-1 rounded-full">⚙️ {msg.text}</span></div>;
            if (isToolCall) return <div key={msg.id} className="flex justify-center"><span className="text-xs text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full">🔧 {msg.text}</span></div>;
            return (
              <div key={msg.id} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${isCustomer ? 'bg-slate-700 text-slate-100 rounded-tl-sm' : 'bg-orange-600 text-white rounded-tr-sm'}`}>
                  <p className="text-sm">{msg.text}</p>
                  <p className={`text-xs mt-1 ${isCustomer ? 'text-slate-500' : 'text-orange-200'}`}>{msg.time}</p>
                </div>
              </div>
            );
          })}
        </div>
        {/* Reply Input (disabled) */}
        <div className="flex gap-2">
          <input disabled placeholder="Ketik balasan..." className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-sm text-slate-500 cursor-not-allowed" />
          <button disabled className="px-4 py-2 bg-slate-700 text-slate-500 text-sm font-medium rounded-lg cursor-not-allowed">Kirim</button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================
// 8. TENANT CONVERSATIONS PAGE
// ============================================
export function TenantConversationsPage() {
  const [selectedConv, setSelectedConv] = React.useState(conversations[0]?.id || '');
  const [waSessionFilter, setWaSessionFilter] = React.useState('all');
  const [detailConv, setDetailConv] = React.useState<typeof conversations[0] | null>(null);

  const conv = conversations.find(c => c.id === selectedConv);

  const filteredConvs = waSessionFilter === 'all' ? conversations : conversations;

  return (
    <div className="space-y-0">
      {/* WA Session Selector */}
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <label className="text-sm text-slate-400 whitespace-nowrap">WA Session:</label>
          <FormSelect value={waSessionFilter} onChange={e => setWaSessionFilter(e.target.value)} className="max-w-xs">
            <option value="all">Semua Session</option>
            {waSessions.map(s => (
              <option key={s.id} value={s.id}>{s.phone} ({s.status})</option>
            ))}
          </FormSelect>
        </div>
      </div>

      <ConversationDetailModal open={!!detailConv} onClose={() => setDetailConv(null)} conv={detailConv} />

      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden flex flex-col md:flex-row" style={{ minHeight: 'calc(100vh - 240px)' }}>
        {/* Conversation List */}
        <div className="w-full md:w-80 border-b md:border-b-0 md:border-r border-slate-700 flex flex-col">
          <div className="p-4 border-b border-slate-700">
            <SearchInput placeholder="Cari percakapan..." onChange={() => {}} />
          </div>
          <div className="flex-1 overflow-y-auto max-h-96 md:max-h-none">
            {filteredConvs.map((c) => (
              <button
                key={c.id}
                onClick={() => setSelectedConv(c.id)}
                className={`w-full text-left p-4 border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors ${selectedConv === c.id ? 'bg-slate-700/50 border-l-2 border-l-orange-500' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-sm font-bold text-orange-400 flex-shrink-0">
                    {c.customer.avatar}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-100 truncate">{c.customer.name}</p>
                      <span className="text-xs text-slate-500 flex-shrink-0 ml-2">{c.time}</span>
                    </div>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{c.last_message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/50 text-slate-400'}`}>
                        {c.status === 'active' ? 'Aktif' : 'Ditutup'}
                      </span>
                      {c.unread > 0 && (
                        <span className="bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{c.unread}</span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Message Detail */}
        <div className="flex-1 flex flex-col">
          {conv ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-slate-700 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center text-sm font-bold text-orange-400">
                  {conv.customer.avatar}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-100">{conv.customer.name}</p>
                  <p className="text-xs text-slate-400">{conv.customer.phone} · via {conv.channel === 'whatsapp' ? '📱 WA' : '✈️ TG'} · Agent: {conv.agent}</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={() => setDetailConv(conv)} className="text-xs text-orange-400 hover:text-orange-300 transition-colors">Lihat Detail →</button>
                  <StatusBadge status={conv.status} />
                </div>
              </div>

              {/* AI Summary */}
              <div className="p-3 bg-violet-500/10 border-b border-violet-500/20">
                <div className="flex items-center gap-2">
                  <span className="text-sm">🧠</span>
                  <p className="text-xs text-violet-400"><span className="font-medium">AI Summary:</span> {conv.ai_summary}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                {conv.messages.map((msg) => {
                  const isCustomer = msg.sender === 'customer';
                  const isSystem = msg.sender === 'system' || msg.type === 'system';
                  const isToolCall = msg.type === 'tool_call';

                  if (isSystem) {
                    return (
                      <div key={msg.id} className="flex justify-center">
                        <span className="text-xs text-slate-500 bg-slate-700/50 px-3 py-1 rounded-full">⚙️ {msg.text}</span>
                      </div>
                    );
                  }
                  if (isToolCall) {
                    return (
                      <div key={msg.id} className="flex justify-center">
                        <span className="text-xs text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full">🔧 {msg.text}</span>
                      </div>
                    );
                  }

                  return (
                    <div key={msg.id} className={`flex ${isCustomer ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${isCustomer ? 'bg-slate-700 text-slate-100 rounded-tl-sm' : 'bg-orange-600 text-white rounded-tr-sm'}`}>
                        <p className="text-sm">{msg.text}</p>
                        <p className={`text-xs mt-1 ${isCustomer ? 'text-slate-500' : 'text-orange-200'}`}>{msg.time}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-slate-700">
                <div className="flex gap-2">
                  <input placeholder="Ketik pesan..." className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50" />
                  <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">Kirim</button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState title="Pilih percakapan" description="Klik percakapan di sebelah kiri untuk melihat detail." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// 9. TENANT BUSINESS PAGE
// ============================================
export function TenantBusinessPage() {
  const [bizTab, setBizTab] = React.useState('retail');
  const [subNav, setSubNav] = React.useState('produk');
  const [search, setSearch] = React.useState('');

  const bizTabs = [
    { label: '🏪 Retail', value: 'retail' },
    { label: '🍽 Restoran', value: 'restoran' },
    { label: '🚗 Rental/Sewa', value: 'rental' },
  ];
  const subNavs = [
    { label: 'Produk', value: 'produk' },
    { label: 'Kategori', value: 'kategori' },
    { label: 'Order', value: 'order' },
    { label: 'Transaksi', value: 'transaksi' },
  ];

  const filtered = retailProducts.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageHeader title="Modul Bisnis" />

      {/* Business Type Tabs */}
      <TabFilter tabs={bizTabs} active={bizTab} onChange={setBizTab} />

      {/* Sub-nav */}
      <div className="flex gap-1 bg-slate-800 rounded-lg p-1 w-fit">
        {subNavs.map(s => (
          <button key={s.value} onClick={() => setSubNav(s.value)}
            className={`px-4 py-2 rounded-md text-xs font-medium transition-all ${subNav === s.value ? 'bg-slate-700 text-slate-100' : 'text-slate-400 hover:text-white'}`}>
            {s.label}
          </button>
        ))}
      </div>

      {subNav === 'produk' && (
        <>
          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400">Total Produk</p>
              <p className="text-xl font-bold text-slate-50">{retailProducts.length}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400">Order Hari Ini</p>
              <p className="text-xl font-bold text-slate-50">12</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400">Revenue Hari Ini</p>
              <p className="text-xl font-bold text-amber-400">{formatRupiah(2450000)}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <p className="text-xs text-slate-400">Low Stock</p>
              <p className="text-xl font-bold text-red-400">{retailProducts.filter(p => p.status === 'low_stock' || p.status === 'out_of_stock').length}</p>
            </div>
          </div>

          {/* Search + Add */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 max-w-sm">
              <SearchInput placeholder="Cari produk..." value={search} onChange={setSearch} />
            </div>
            <button className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
              + Tambah Produk
            </button>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <div key={p.id} className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden hover:border-slate-600 transition-all">
                <div className="h-32 bg-slate-700 flex items-center justify-center">
                  <span className="text-4xl text-slate-600">📦</span>
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-100">{p.name}</h3>
                    <StatusBadge status={p.status} />
                  </div>
                  <p className="text-lg font-bold text-amber-400 mb-1">{formatRupiah(p.price)}</p>
                  <p className="text-xs text-slate-400 mb-3">Stok: {p.stock} · {p.category}</p>
                  <button className="w-full py-2 text-xs font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {subNav !== 'produk' && (
        <EmptyState title={`${subNav.charAt(0).toUpperCase() + subNav.slice(1)}`} description="Fitur ini akan tersedia segera." />
      )}
    </div>
  );
}

// ============================================
// Integration CRUD Modals
// ============================================
function SpreadsheetFormModal({ open, onClose, sp, onSave }: {
  open: boolean; onClose: () => void;
  sp: typeof integrations.spreadsheets[0] | null;
  onSave: () => void;
}) {
  const { toast, show } = useToast();
  const [name, setName] = React.useState(sp?.name || '');
  const [target, setTarget] = React.useState(sp?.target || 'customers');
  const [syncType, setSyncType] = React.useState(sp?.sync_type || 'auto');
  const [url, setUrl] = React.useState('');
  React.useEffect(() => {
    if (sp) { setName(sp.name); setTarget(sp.target); setSyncType(sp.sync_type); }
    else { setName(''); setTarget('customers'); setSyncType('auto'); setUrl(''); }
  }, [sp, open]);
  const handleSave = () => { onSave(); onClose(); show(sp ? 'Spreadsheet diperbarui!' : 'Spreadsheet ditambahkan!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title={sp ? 'Edit Spreadsheet' : 'Hubungkan Google Sheet'} footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Nama" required><FormInput value={name} onChange={e => setName(e.target.value)} /></FormField>
          <FormField label="Target"><FormSelect value={target} onChange={e => setTarget(e.target.value)}><option value="customers">Customers</option><option value="products">Products</option><option value="orders">Orders</option></FormSelect></FormField>
          <FormField label="Sync Type"><FormSelect value={syncType} onChange={e => setSyncType(e.target.value)}><option value="auto">Auto</option><option value="manual">Manual</option></FormSelect></FormField>
          {!sp && <FormField label="Google Sheet URL"><FormInput value={url} onChange={e => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..." /></FormField>}
        </div>
      </Modal>
    </>
  );
}

function WebhookFormModal({ open, onClose, wh, onSave }: {
  open: boolean; onClose: () => void;
  wh: typeof integrations.webhooks[0] | null;
  onSave: () => void;
}) {
  const { toast, show } = useToast();
  const [name, setName] = React.useState(wh?.name || '');
  const [trigger, setTrigger] = React.useState(wh?.trigger || 'order.created');
  const [url, setUrl] = React.useState(wh?.url || '');
  const [secret, setSecret] = React.useState('');
  React.useEffect(() => {
    if (wh) { setName(wh.name); setTrigger(wh.trigger); setUrl(wh.url); }
    else { setName(''); setTrigger('order.created'); setUrl(''); setSecret(''); }
  }, [wh, open]);
  const handleSave = () => { onSave(); onClose(); show(wh ? 'Webhook diperbarui!' : 'Webhook ditambahkan!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title={wh ? 'Edit Webhook' : 'Buat Webhook Baru'} footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Nama" required><FormInput value={name} onChange={e => setName(e.target.value)} /></FormField>
          <FormField label="Trigger Event"><FormSelect value={trigger} onChange={e => setTrigger(e.target.value)}>
            <option value="order.created">order.created</option><option value="customer.created">customer.created</option><option value="payment.success">payment.success</option><option value="payment.failed">payment.failed</option><option value="refund.requested">refund.requested</option>
          </FormSelect></FormField>
          <FormField label="URL" required><FormInput value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." /></FormField>
          {!wh && <FormField label="Secret Key"><FormInput value={secret} onChange={e => setSecret(e.target.value)} placeholder="whsec_..." /></FormField>}
        </div>
      </Modal>
    </>
  );
}

function ScheduleFormModal({ open, onClose, sc, onSave }: {
  open: boolean; onClose: () => void;
  sc: typeof integrations.schedules[0] | null;
  onSave: () => void;
}) {
  const { toast, show } = useToast();
  const [name, setName] = React.useState(sc?.name || '');
  const [cron, setCron] = React.useState(sc?.cron || '');
  const [desc, setDesc] = React.useState(sc?.description || '');
  const [isActive, setIsActive] = React.useState(sc?.status === 'active' || true);
  React.useEffect(() => {
    if (sc) { setName(sc.name); setCron(sc.cron); setDesc(sc.description); setIsActive(sc.status === 'active'); }
    else { setName(''); setCron(''); setDesc(''); setIsActive(true); }
  }, [sc, open]);
  const handleSave = () => { onSave(); onClose(); show(sc ? 'Schedule diperbarui!' : 'Schedule ditambahkan!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title={sc ? 'Edit Schedule' : 'Buat Schedule Baru'} footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Nama" required><FormInput value={name} onChange={e => setName(e.target.value)} /></FormField>
          <FormField label="Cron Expression" required><FormInput value={cron} onChange={e => setCron(e.target.value)} placeholder="0 6 * * *" /></FormField>
          <FormField label="Deskripsi"><FormTextarea rows={2} value={desc} onChange={e => setDesc(e.target.value)} /></FormField>
          <Toggle checked={isActive} onChange={setIsActive} label="Aktif" />
        </div>
      </Modal>
    </>
  );
}

// ============================================
// 10. TENANT INTEGRATIONS PAGE
// ============================================
export function TenantIntegrationsPage() {
  const { toast, show } = useToast();
  const [tab, setTab] = React.useState('sheets');
  const [editSp, setEditSp] = React.useState<typeof integrations.spreadsheets[0] | null>(null);
  const [createSp, setCreateSp] = React.useState(false);
  const [editWh, setEditWh] = React.useState<typeof integrations.webhooks[0] | null>(null);
  const [createWh, setCreateWh] = React.useState(false);
  const [editSc, setEditSc] = React.useState<typeof integrations.schedules[0] | null>(null);
  const [createSc, setCreateSc] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  const tabs = [
    { label: '📊 Google Sheets', value: 'sheets' },
    { label: '🔗 Webhook', value: 'webhook' },
    { label: '⏰ Schedule', value: 'schedule' },
  ];

  return (
    <div className="space-y-6">
      <Toast toast={toast} />
      <SpreadsheetFormModal open={createSp} onClose={() => setCreateSp(false)} sp={null} onSave={() => {}} />
      <SpreadsheetFormModal open={!!editSp} onClose={() => setEditSp(null)} sp={editSp} onSave={() => {}} />
      <WebhookFormModal open={createWh} onClose={() => setCreateWh(false)} wh={null} onSave={() => {}} />
      <WebhookFormModal open={!!editWh} onClose={() => setEditWh(null)} wh={editWh} onSave={() => {}} />
      <ScheduleFormModal open={createSc} onClose={() => setCreateSc(false)} sc={null} onSave={() => {}} />
      <ScheduleFormModal open={!!editSc} onClose={() => setEditSc(null)} sc={editSc} onSave={() => {}} />
      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); show('Berhasil dihapus!'); }} title="Hapus?" message="Apakah Anda yakin ingin menghapus item ini?" />

      <PageHeader title="Integrasi" />

      <TabFilter tabs={tabs} active={tab} onChange={setTab} />

      {tab === 'sheets' && (
        <div className="space-y-4">
          {integrations.spreadsheets.map((sp) => (
            <div key={sp.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex items-center justify-between flex-wrap gap-4 hover:border-slate-600 transition-all">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center text-2xl">📊</div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-100">{sp.name}</h3>
                  <p className="text-xs text-slate-400">Target: {sp.target} · Sync: {sp.sync_type} · {sp.last_sync}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={sp.status} />
                <DropdownMenu trigger={<button className="text-slate-400 hover:text-white p-1"><IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></IconSvg></button>}>
                  <DropdownItem onClick={() => setEditSp(sp)}>Edit</DropdownItem>
                  <DropdownItem onClick={() => show('Export dimulai...')}>Export Now</DropdownItem>
                  <DropdownItem onClick={() => show('Import dimulai...')}>Import</DropdownItem>
                  <DropdownItem danger onClick={() => setConfirmDelete(true)}>Hapus</DropdownItem>
                </DropdownMenu>
              </div>
            </div>
          ))}
          <button onClick={() => setCreateSp(true)} className="w-full border-2 border-dashed border-slate-700 rounded-xl p-4 text-slate-500 hover:border-orange-500/50 hover:text-orange-400 transition-all text-sm">
            + Hubungkan Google Sheet Baru
          </button>
        </div>
      )}

      {tab === 'webhook' && (
        <div className="space-y-4">
          {integrations.webhooks.map((wh) => (
            <div key={wh.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-100">{wh.name}</h3>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${wh.status_code === 200 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                    {wh.status_code}
                  </span>
                  <DropdownMenu trigger={<button className="text-slate-400 hover:text-white p-1"><IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></IconSvg></button>}>
                    <DropdownItem onClick={() => setEditWh(wh)}>Edit</DropdownItem>
                    <DropdownItem danger onClick={() => setConfirmDelete(true)}>Hapus</DropdownItem>
                  </DropdownMenu>
                </div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex gap-2"><span className="text-slate-500 w-20">Trigger:</span><span className="text-slate-300 font-mono">{wh.trigger}</span></div>
                <div className="flex gap-2"><span className="text-slate-500 w-20">URL:</span><span className="text-slate-300 font-mono truncate">{wh.url}</span></div>
                <div className="flex gap-2"><span className="text-slate-500 w-20">Terakhir:</span><span className="text-slate-300">{wh.last_triggered}</span></div>
              </div>
            </div>
          ))}
          <button onClick={() => setCreateWh(true)} className="w-full border-2 border-dashed border-slate-700 rounded-xl p-4 text-slate-500 hover:border-orange-500/50 hover:text-orange-400 transition-all text-sm">
            + Buat Webhook Baru
          </button>
        </div>
      )}

      {tab === 'schedule' && (
        <div className="space-y-4">
          {integrations.schedules.map((sc) => (
            <div key={sc.id} className="bg-slate-800 border border-slate-700 rounded-xl p-5 flex items-center justify-between flex-wrap gap-4 hover:border-slate-600 transition-all">
              <div>
                <h3 className="text-sm font-semibold text-slate-100">{sc.name}</h3>
                <p className="text-xs text-slate-400 mt-1">{sc.description}</p>
                <p className="text-xs text-slate-500 mt-1 font-mono">{sc.cron}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={sc.status} />
                <DropdownMenu trigger={<button className="text-slate-400 hover:text-white p-1"><IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></IconSvg></button>}>
                  <DropdownItem onClick={() => setEditSc(sc)}>Edit</DropdownItem>
                  <DropdownItem danger onClick={() => setConfirmDelete(true)}>Hapus</DropdownItem>
                </DropdownMenu>
              </div>
            </div>
          ))}
          <button onClick={() => setCreateSc(true)} className="w-full border-2 border-dashed border-slate-700 rounded-xl p-4 text-slate-500 hover:border-orange-500/50 hover:text-orange-400 transition-all text-sm">
            + Buat Schedule Baru
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// Tool Approval Detail + Reject Modals
// ============================================
function ApprovalDetailModal({ open, onClose, approval, onApprove, onReject }: {
  open: boolean; onClose: () => void;
  approval: typeof toolApprovals[0] | null;
  onApprove: () => void; onReject: () => void;
}) {
  if (!approval) return null;
  return (
    <Modal open={open} onClose={onClose} title="Detail Tool Approval" size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-slate-900/50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Tool</p><p className="text-sm font-medium text-slate-200">🔧 {approval.tool_name}</p></div>
          <div className="bg-slate-900/50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Agent</p><p className="text-sm font-medium text-violet-400">{approval.agent}</p></div>
          <div className="bg-slate-900/50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Pelanggan</p><p className="text-sm font-medium text-slate-200">{approval.customer.name}</p></div>
          <div className="bg-slate-900/50 rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Waktu</p><p className="text-sm font-medium text-slate-200">{approval.created_at}</p></div>
        </div>
        <div><p className="text-xs text-slate-500 mb-1.5">Telepon Pelanggan</p><p className="text-sm text-slate-300">{approval.customer.phone}</p></div>
        <div><p className="text-xs text-slate-500 mb-1.5">Parameters (JSON)</p><div className="bg-slate-900 rounded-lg p-3"><pre className="text-xs text-emerald-400 font-mono whitespace-pre-wrap">{JSON.stringify(approval.params, null, 2)}</pre></div></div>
        <div><p className="text-xs text-slate-500 mb-1">Status</p><StatusBadge status={approval.status} /></div>
        {approval.status === 'pending' && (
          <div className="flex gap-2 pt-2">
            <button onClick={() => { onReject(); onClose(); }} className="px-4 py-2 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors">❌ Tolak</button>
            <button onClick={() => { onApprove(); onClose(); }} className="px-4 py-2 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors">✅ Setujui Eksekusi</button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function RejectModal({ open, onClose, onConfirm }: {
  open: boolean; onClose: () => void; onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = React.useState('');
  const handleReject = () => { onConfirm(reason); setReason(''); onClose(); };
  return (
    <Modal open={open} onClose={onClose} title="Tolak Approval" size="sm" footer={
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
        <button onClick={handleReject} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors">Tolak</button>
      </div>
    }>
      <FormField label="Alasan Penolakan" required><FormTextarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Tuliskan alasan..." /></FormField>
    </Modal>
  );
}

// ============================================
// 11. TENANT TOOL APPROVALS PAGE
// ============================================
export function TenantToolApprovalsPage() {
  const { toast, show } = useToast();
  const [filter, setFilter] = React.useState('pending');
  const pendingCount = toolApprovals.filter(t => t.status === 'pending').length;
  const [viewApproval, setViewApproval] = React.useState<typeof toolApprovals[0] | null>(null);
  const [rejectApproval, setRejectApproval] = React.useState<typeof toolApprovals[0] | null>(null);

  const allApprovals = [
    ...toolApprovals,
    { id: 'ta-004', tool_name: 'cek_stok', agent: 'Asisten Toko Budi', customer: { name: 'Citra Sari', phone: '+62814567890' }, params: { product_id: 'prod-002' }, status: 'approved' as const, created_at: '2 jam lalu' },
    { id: 'ta-005', tool_name: 'refund_pesanan', agent: 'Budi Info Bot', customer: { name: 'Eko Prasetyo', phone: '+62816789012' }, params: { order_id: 'ord-456', reason: 'tidak sesuai' }, status: 'rejected' as const, created_at: '3 jam lalu' },
  ];

  const allFiltered = filter === 'all' ? allApprovals :
    filter === 'pending' ? allApprovals.filter(t => t.status === 'pending') :
    filter === 'approved' ? allApprovals.filter(t => t.status === 'approved') :
    allApprovals.filter(t => t.status === 'rejected');

  const tabs = [
    { label: 'Pending', value: 'pending', count: pendingCount },
    { label: 'Disetujui', value: 'approved' },
    { label: 'Ditolak', value: 'rejected' },
    { label: 'Semua', value: 'all' },
  ];

  const handleApprove = () => show('Approval disetujui!');
  const handleReject = (_reason: string) => show('Approval ditolak!');

  return (
    <div className="space-y-6">
      <Toast toast={toast} />
      <ApprovalDetailModal open={!!viewApproval} onClose={() => setViewApproval(null)} approval={viewApproval} onApprove={handleApprove} onReject={() => setRejectApproval(viewApproval)} />
      <RejectModal open={!!rejectApproval} onClose={() => setRejectApproval(null)} onConfirm={handleReject} />

      <PageHeader title={`Tool Approvals · ${pendingCount} menunggu`} />

      <TabFilter tabs={tabs} active={filter} onChange={setFilter} />

      <div className="space-y-4">
        {allFiltered.map((ta) => (
          <div key={ta.id} onClick={() => setViewApproval(ta)} className="bg-slate-800 border border-slate-700 rounded-xl p-5 hover:border-slate-600 transition-all cursor-pointer">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-lg">🔧</span>
                  <h3 className="text-sm font-semibold text-slate-100">{ta.tool_name}</h3>
                  <StatusBadge status={ta.status} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mb-3">
                  <div>
                    <span className="text-slate-500">Agent:</span>{' '}
                    <span className="text-violet-400">{ta.agent}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Pelanggan:</span>{' '}
                    <span className="text-slate-300">{ta.customer.name} ({ta.customer.phone})</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Waktu:</span>{' '}
                    <span className="text-slate-300">{ta.created_at}</span>
                  </div>
                </div>
                <div className="bg-slate-900 rounded-lg p-3">
                  <p className="text-xs text-slate-500 mb-1">Parameters:</p>
                  <pre className="text-xs text-emerald-400 font-mono">{JSON.stringify(ta.params, null, 2)}</pre>
                </div>
              </div>

              {ta.status === 'pending' && (
                <div className="flex gap-2 lg:flex-col" onClick={e => e.stopPropagation()}>
                  <button onClick={() => setRejectApproval(ta)} className="px-4 py-2 text-xs font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors whitespace-nowrap">
                    ❌ Tolak
                  </button>
                  <button onClick={handleApprove} className="px-4 py-2 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors whitespace-nowrap">
                    ✅ Setujui Eksekusi
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {allFiltered.length === 0 && (
          <EmptyState title="Tidak ada approval" description="Semua tool approval sudah diproses." />
        )}
      </div>
    </div>
  );
}

// ============================================
// Subscription Modals
// ============================================
function UpgradePlanModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast, show } = useToast();
  const [selectedPlan, setSelectedPlan] = React.useState('business');
  const currentPlan = plans.find(p => p.slug === currentTenant.plan);
  const chosenPlan = plans.find(p => p.slug === selectedPlan);
  const priceDiff = chosenPlan && currentPlan ? chosenPlan.price_monthly - currentPlan.price_monthly : 0;

  const handleUpgrade = () => { onClose(); show('Plan berhasil di-upgrade!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title="Upgrade Plan" subtitle="Pilih plan baru Anda" size="lg" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleUpgrade} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Upgrade Sekarang</button>
        </div>
      }>
        <div className="space-y-4">
          <div className="bg-slate-900/50 rounded-lg p-4">
            <p className="text-xs text-slate-500 mb-1">Plan Saat Ini</p>
            <p className="text-lg font-bold text-slate-200">{currentPlan?.name} — {formatRupiah(currentPlan?.price_monthly || 0)}/bulan</p>
          </div>
          <div className="space-y-3">
            {plans.filter(p => p.slug !== currentTenant.plan).map(p => (
              <button key={p.id} onClick={() => setSelectedPlan(p.slug)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPlan === p.slug ? 'border-orange-500 bg-orange-500/5' : 'border-slate-700 hover:border-slate-600'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-200">{p.name}</h3>
                    {p.is_popular && <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full">Popular</span>}
                  </div>
                  <p className="text-sm font-bold text-amber-400">{formatRupiah(p.price_monthly)}/bln</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  {p.features.slice(0, 3).map((f, i) => (
                    <span key={i} className="text-xs text-slate-400">✓ {f}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
          {chosenPlan && priceDiff > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
              <p className="text-sm text-amber-400">Selisih biaya: <span className="font-bold">+{formatRupiah(priceDiff)}/bulan</span></p>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

function TopupTokenModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast, show } = useToast();
  const [amount, setAmount] = React.useState('10000');
  const packages = [
    { tokens: 10000, label: '10K', price: 15000 },
    { tokens: 50000, label: '50K', price: 65000 },
    { tokens: 100000, label: '100K', price: 120000 },
  ];
  const pkg = packages.find(p => p.tokens === Number(amount));
  const handleBuy = () => { onClose(); show('Token berhasil ditopup!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title="Topup Token" subtitle="Tambah kuota token AI Anda" size="default" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleBuy} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Beli Token</button>
        </div>
      }>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            {packages.map(p => (
              <button key={p.tokens} onClick={() => setAmount(String(p.tokens))}
                className={`p-4 rounded-xl border-2 text-center transition-all ${Number(amount) === p.tokens ? 'border-orange-500 bg-orange-500/5' : 'border-slate-700 hover:border-slate-600'}`}>
                <p className="text-lg font-bold text-slate-100">{p.label}</p>
                <p className="text-xs text-slate-400">tokens</p>
              </button>
            ))}
          </div>
          {pkg && (
            <div className="bg-slate-900/50 rounded-lg p-4 text-center">
              <p className="text-xs text-slate-500 mb-1">Harga</p>
              <p className="text-xl font-bold text-amber-400">{formatRupiah(pkg.price)}</p>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

function ViewInvoiceModal({ open, onClose, invoice }: {
  open: boolean; onClose: () => void;
  invoice: typeof invoices[0] | null;
}) {
  if (!invoice) return null;
  const items = [
    { label: 'Invoice ID', value: invoice.id },
    { label: 'Tenant', value: currentTenant.name },
    { label: 'Plan', value: invoice.plan },
    { label: 'Periode', value: invoice.period },
    { label: 'Jumlah', value: formatRupiah(invoice.amount) },
    { label: 'Status', value: invoice.status === 'paid' ? 'Lunas' : 'Belum Bayar' },
    { label: 'Metode Pembayaran', value: 'Midtrans (Virtual Account)' },
    { label: 'Tanggal Bayar', value: invoice.date },
  ];
  return (
    <Modal open={open} onClose={onClose} title="Detail Invoice" subtitle={invoice.id} size="default">
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="flex justify-between py-2 border-b border-slate-700/50">
            <span className="text-sm text-slate-400">{item.label}</span>
            <span className="text-sm text-slate-200 font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    </Modal>
  );
}

// ============================================
// 12. TENANT SUBSCRIPTION PAGE
// ============================================
export function TenantSubscriptionPage() {
  const navigate = useRouter((s) => s.navigate);
  const { toast, show } = useToast();
  const [showUpgrade, setShowUpgrade] = React.useState(false);
  const [showTopup, setShowTopup] = React.useState(false);
  const [viewInvoice, setViewInvoice] = React.useState<typeof invoices[0] | null>(null);

  const invColumns = [
    { key: 'id', label: 'ID' },
    { key: 'plan', label: 'Plan' },
    {
      key: 'amount', label: 'Amount',
      render: (inv: typeof invoices[0]) => <span className="text-amber-400">{formatRupiah(inv.amount)}</span>,
    },
    {
      key: 'status', label: 'Status',
      render: (inv: typeof invoices[0]) => <StatusBadge status={inv.status} />,
    },
    { key: 'date', label: 'Date' },
  ];

  return (
    <div className="space-y-6">
      <Toast toast={toast} />
      <UpgradePlanModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
      <TopupTokenModal open={showTopup} onClose={() => setShowTopup(false)} />
      <ViewInvoiceModal open={!!viewInvoice} onClose={() => setViewInvoice(null)} invoice={viewInvoice} />

      <PageHeader title="Langganan" />

      {/* Active Plan Banner */}
      <div className="relative bg-gradient-to-r from-orange-600 to-amber-500 rounded-2xl p-6 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(255,255,255,0.1),transparent)]" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold">{currentTenant.plan_name}</h2>
              <span className="px-2.5 py-0.5 bg-white/20 rounded-full text-xs font-medium">Active</span>
            </div>
            <p className="text-orange-100 text-sm">{formatRupiah(299000)}/bulan · Berakhir {currentTenant.subscription_end} ({currentTenant.days_remaining} hari lagi)</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowUpgrade(true)} className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded-lg transition-colors backdrop-blur-sm">
              Upgrade Plan
            </button>
            <button className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg transition-colors backdrop-blur-sm">
              Cancel Langganan
            </button>
          </div>
        </div>
      </div>

      {/* Token Balance */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-base font-semibold text-slate-100">Token Balance</h3>
            <p className="text-xs text-slate-400 mt-1">Reset setiap tanggal 1 bulan berikutnya</p>
          </div>
          <button onClick={() => setShowTopup(true)} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
            + Topup Token
          </button>
        </div>
        <div className="flex items-center gap-4 mb-2">
          <span className="text-2xl font-bold text-slate-50">{formatNumber(tenantAnalytics.token_usage.used)}</span>
          <span className="text-slate-500">/</span>
          <span className="text-lg text-slate-400">{formatNumber(tenantAnalytics.token_usage.total)} tokens</span>
        </div>
        <QuotaBar used={tenantAnalytics.token_usage.used} total={tenantAnalytics.token_usage.total} />
      </div>

      {/* Invoice History */}
      <div>
        <h3 className="text-base font-semibold text-slate-100 mb-4">Riwayat Invoice</h3>
        <DataTable columns={invColumns} data={invoices} onRowClick={(inv) => setViewInvoice(inv as typeof invoices[0])} />
      </div>
    </div>
  );
}

// ============================================
// Team Modals
// ============================================
function InviteMemberModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast, show } = useToast();
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState('Staff');
  const [perms, setPerms] = React.useState<Record<string, boolean>>({});
  const permCategories = [
    { cat: 'Agent', perms: ['agent.view', 'agent.create', 'agent.manage'] },
    { cat: 'Pelanggan', perms: ['customer.view', 'customer.manage'] },
    { cat: 'Percakapan', perms: ['conversation.view', 'conversation.reply'] },
    { cat: 'Integrasi', perms: ['integration.view', 'integration.manage'] },
  ];
  const handleSave = () => { onClose(); setEmail(''); setPerms({}); show('Undangan berhasil dikirim!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title="Undang Member Baru" size="lg" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Kirim Undangan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Email" required><FormInput type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@contoh.com" /></FormField>
          <FormField label="Role">
            <FormSelect value={role} onChange={e => setRole(e.target.value)}><option value="Owner">Owner</option><option value="Admin">Admin</option><option value="Staff">Staff</option></FormSelect>
          </FormField>
          <div>
            <p className="text-sm font-medium text-slate-300 mb-3">Permissions</p>
            {permCategories.map(pc => (
              <div key={pc.cat} className="mb-3">
                <p className="text-xs text-slate-500 mb-1.5 font-medium uppercase">{pc.cat}</p>
                <div className="flex flex-wrap gap-2">
                  {pc.perms.map(p => (
                    <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!!perms[p]} onChange={e => setPerms({ ...perms, [p]: e.target.checked })} className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500/50" />
                      <span className="text-xs text-slate-300">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}

function EditMemberModal({ open, onClose, member }: {
  open: boolean; onClose: () => void;
  member: typeof teamMembers[0] | null;
}) {
  const { toast, show } = useToast();
  const [name, setName] = React.useState(member?.name || '');
  const [role, setRole] = React.useState(member?.role || 'Staff');
  const permCategories = [
    { cat: 'Agent', perms: ['agent.view', 'agent.create', 'agent.manage'] },
    { cat: 'Pelanggan', perms: ['customer.view', 'customer.manage'] },
    { cat: 'Percakapan', perms: ['conversation.view', 'conversation.reply'] },
    { cat: 'Integrasi', perms: ['integration.view', 'integration.manage'] },
  ];
  const [perms, setPerms] = React.useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    if (member) member.permissions.forEach(p => { init[p] = true; });
    return init;
  });
  React.useEffect(() => {
    if (member) { setName(member.name); setRole(member.role); const init: Record<string, boolean> = {}; member.permissions.forEach(p => { init[p] = true; }); setPerms(init); }
  }, [member, open]);
  const handleSave = () => { onClose(); show('Member berhasil diperbarui!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title="Edit Member" subtitle={member?.email} size="lg" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Nama"><FormInput value={name} onChange={e => setName(e.target.value)} /></FormField>
          <FormField label="Email"><FormInput value={member?.email || ''} disabled /></FormField>
          <FormField label="Role">
            <FormSelect value={role} onChange={e => setRole(e.target.value)}><option value="Owner">Owner</option><option value="Admin">Admin</option><option value="Staff">Staff</option></FormSelect>
          </FormField>
          <div>
            <p className="text-sm font-medium text-slate-300 mb-3">Permissions</p>
            {permCategories.map(pc => (
              <div key={pc.cat} className="mb-3">
                <p className="text-xs text-slate-500 mb-1.5 font-medium uppercase">{pc.cat}</p>
                <div className="flex flex-wrap gap-2">
                  {pc.perms.map(p => (
                    <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!!perms[p]} onChange={e => setPerms({ ...perms, [p]: e.target.checked })} className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500/50" />
                      <span className="text-xs text-slate-300">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}

function ViewMemberModal({ open, onClose, member }: {
  open: boolean; onClose: () => void;
  member: typeof teamMembers[0] | null;
}) {
  if (!member) return null;
  const activityLog = ['Login ke dashboard', 'Edit agent "Survey Bot"', 'Lihat percakapan conv-003'];
  return (
    <Modal open={open} onClose={onClose} title="Detail Member" size="lg">
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-orange-500/20 flex items-center justify-center text-lg font-bold text-orange-400">{member.name.split(' ').map(n => n[0]).join('')}</div>
          <div>
            <p className="text-base font-semibold text-slate-100">{member.name}</p>
            <p className="text-sm text-slate-400">{member.email}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${member.role === 'Owner' ? 'bg-amber-500/20 text-amber-400' : member.role === 'Admin' ? 'bg-violet-500/20 text-violet-400' : 'bg-slate-600/50 text-slate-400'}`}>{member.role}</span>
              <StatusBadge status={member.status === 'active' ? 'active' : 'pending'} />
            </div>
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-300 mb-2">Permissions</p>
          <div className="flex flex-wrap gap-1.5">
            {member.permissions.map((p, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-slate-700 text-slate-300 rounded">{p}</span>
            ))}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-300 mb-2">Activity Log</p>
          <div className="space-y-1">
            {activityLog.map((log, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                <span className="text-slate-600">{String(i + 1).padStart(2, '0')}</span>
                <span>{log}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function CreateRoleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast, show } = useToast();
  const [roleName, setRoleName] = React.useState('');
  const [perms, setPerms] = React.useState<Record<string, boolean>>({});
  const permCategories = [
    { cat: 'Agent', perms: ['agent.view', 'agent.create', 'agent.manage'] },
    { cat: 'Pelanggan', perms: ['customer.view', 'customer.manage'] },
    { cat: 'Percakapan', perms: ['conversation.view', 'conversation.reply'] },
    { cat: 'Integrasi', perms: ['integration.view', 'integration.manage'] },
  ];
  const handleSave = () => { onClose(); show('Role berhasil dibuat!'); };
  return (
    <>
      <Toast toast={toast} />
      <Modal open={open} onClose={onClose} title="Buat Role Baru" size="default" footer={
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">Batal</button>
          <button onClick={handleSave} className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">Simpan</button>
        </div>
      }>
        <div className="space-y-4">
          <FormField label="Nama Role" required><FormInput value={roleName} onChange={e => setRoleName(e.target.value)} placeholder="mis. Editor" /></FormField>
          <div>
            <p className="text-sm font-medium text-slate-300 mb-3">Permissions</p>
            {permCategories.map(pc => (
              <div key={pc.cat} className="mb-3">
                <p className="text-xs text-slate-500 mb-1.5 font-medium uppercase">{pc.cat}</p>
                <div className="flex flex-wrap gap-2">
                  {pc.perms.map(p => (
                    <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={!!perms[p]} onChange={e => setPerms({ ...perms, [p]: e.target.checked })} className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500/50" />
                      <span className="text-xs text-slate-300">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </>
  );
}

// ============================================
// 13. TENANT TEAM PAGE
// ============================================
export function TenantTeamPage() {
  const { toast, show } = useToast();
  const [showInvite, setShowInvite] = React.useState(false);
  const [viewMember, setViewMember] = React.useState<typeof teamMembers[0] | null>(null);
  const [editMember, setEditMember] = React.useState<typeof teamMembers[0] | null>(null);
  const [showCreateRole, setShowCreateRole] = React.useState(false);
  const [confirmRemove, setConfirmRemove] = React.useState(false);

  const columns = [
    {
      key: 'name', label: 'Member',
      render: (m: typeof teamMembers[0]) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-xs font-bold text-orange-400">
            {m.name.split(' ').map(n => n[0]).join('')}
          </div>
          <div>
            <p className="text-sm font-medium text-slate-100">{m.name}</p>
            <p className="text-xs text-slate-500">{m.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role', label: 'Role',
      render: (m: typeof teamMembers[0]) => (
        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
          m.role === 'Owner' ? 'bg-amber-500/20 text-amber-400' :
          m.role === 'Admin' ? 'bg-violet-500/20 text-violet-400' :
          'bg-slate-600/50 text-slate-400'
        }`}>
          {m.role}
        </span>
      ),
    },
    {
      key: 'permissions', label: 'Permissions',
      render: (m: typeof teamMembers[0]) => (
        <div className="flex flex-wrap gap-1">
          {m.permissions.map((p, i) => (
            <span key={i} className="text-xs px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded">{p}</span>
          ))}
        </div>
      ),
    },
    {
      key: 'status', label: 'Status',
      render: (m: typeof teamMembers[0]) => <StatusBadge status={m.status === 'active' ? 'active' : 'pending'} />,
    },
    {
      key: 'joined', label: 'Joined',
      render: (m: typeof teamMembers[0]) => <span className="text-slate-300 text-sm">{m.joined || '-'}</span>,
    },
    {
      key: 'actions', label: '',
      render: (m: typeof teamMembers[0]) => (
        <DropdownMenu trigger={
          <button className="text-slate-400 hover:text-white transition-colors p-1">
            <IconSvg><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></IconSvg>
          </button>
        }>
          <DropdownItem onClick={() => setViewMember(m)}>View</DropdownItem>
          <DropdownItem onClick={() => setEditMember(m)}>Edit</DropdownItem>
          <DropdownItem danger onClick={() => setConfirmRemove(true)}>Remove</DropdownItem>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <Toast toast={toast} />
      <InviteMemberModal open={showInvite} onClose={() => setShowInvite(false)} />
      <EditMemberModal open={!!editMember} onClose={() => setEditMember(null)} member={editMember} />
      <ViewMemberModal open={!!viewMember} onClose={() => setViewMember(null)} member={viewMember} />
      <CreateRoleModal open={showCreateRole} onClose={() => setShowCreateRole(false)} />
      <ConfirmDialog open={confirmRemove} onClose={() => setConfirmRemove(false)} onConfirm={() => { setConfirmRemove(false); show('Member berhasil dihapus!'); }} title="Hapus Member?" message="Apakah Anda yakin ingin menghapus member ini?" />

      <PageHeader title="Tim & Role" actions={
        <div className="flex gap-2">
          <button onClick={() => setShowCreateRole(true)} className="px-4 py-2 text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors">
            + Buat Role
          </button>
          <button onClick={() => setShowInvite(true)} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors">
            + Undang Member
          </button>
        </div>
      } />

      <DataTable columns={columns} data={teamMembers} />
    </div>
  );
}
